// AI Feature Usage Tracker - Track daily limits

const STORAGE_KEY = 'ai_feature_usage';

// Get today's date as a string (YYYY-MM-DD)
const getTodayKey = () => {
  const today = new Date();
  return today.toISOString().split('T')[0];
};

// Get usage data from localStorage
const getUsageData = () => {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    if (!data) return {};
    
    const parsed = JSON.parse(data);
    const today = getTodayKey();
    
    // Reset if it's a new day
    if (parsed.date !== today) {
      return { date: today, usage: {} };
    }
    
    return parsed;
  } catch (error) {
    console.error('Error reading usage data:', error);
    return { date: getTodayKey(), usage: {} };
  }
};

// Save usage data to localStorage
const saveUsageData = (data) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (error) {
    console.error('Error saving usage data:', error);
  }
};

// Check if user can use a feature
export const canUseFeature = (featureName, limit) => {
  // Unlimited (-1) always allowed
  if (limit === -1) return true;
  
  const data = getUsageData();
  const currentUsage = data.usage[featureName] || 0;
  
  return currentUsage < limit;
};

// Get remaining uses for a feature
export const getRemainingUses = (featureName, limit) => {
  // Unlimited
  if (limit === -1) return -1;
  
  const data = getUsageData();
  const currentUsage = data.usage[featureName] || 0;
  const remaining = Math.max(0, limit - currentUsage);
  
  return remaining;
};

// Increment usage count for a feature
export const incrementUsage = (featureName) => {
  const data = getUsageData();
  
  if (!data.usage[featureName]) {
    data.usage[featureName] = 0;
  }
  
  data.usage[featureName]++;
  saveUsageData(data);
  
  return data.usage[featureName];
};

// Get current usage for a feature
export const getCurrentUsage = (featureName) => {
  const data = getUsageData();
  return data.usage[featureName] || 0;
};

// Reset all usage (for testing or manual reset)
export const resetUsage = () => {
  const data = {
    date: getTodayKey(),
    usage: {}
  };
  saveUsageData(data);
};

// Get all usage stats
export const getUsageStats = () => {
  const data = getUsageData();
  return {
    date: data.date,
    usage: data.usage || {}
  };
};

export default {
  canUseFeature,
  getRemainingUses,
  incrementUsage,
  getCurrentUsage,
  resetUsage,
  getUsageStats
};
